import React, { useState, useEffect } from 'react';

const AppFormModal = ({ app, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    icon: '🔧',
    color: '#3B82F6',
    category: '',
    port: '',
    active: true,
    requiresAuth: true,
  });

  const [errors, setErrors] = useState({});

  // Réinitialiser le formulaire quand `app` ou `isOpen` change
  useEffect(() => {
    if (isOpen) {
      if (app) {
        // Mode édition : charger les données existantes
        setFormData({
          name: app.name || '',
          description: app.description || '',
          url: app.url || '',
          icon: app.icon || '🔧',
          color: app.color || '#3B82F6',
          category: app.category || '',
          port: app.port?.toString() || '',
          active: app.active !== undefined ? app.active : true,
          requiresAuth: app.requiresAuth !== undefined ? app.requiresAuth : true,
        });
      } else {
        // Mode création : réinitialiser le formulaire
        setFormData({
          name: '',
          description: '',
          url: '',
          icon: '🔧',
          color: '#3B82F6',
          category: '',
          port: '',
          active: true,
          requiresAuth: true,
        });
      }
      setErrors({});
    }
  }, [app, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.url.trim()) {
      newErrors.url = 'L\'URL est requise';
    } else if (!formData.url.startsWith('http')) {
      newErrors.url = 'L\'URL doit commencer par http:// ou https://';
    }

    if (!formData.port) {
      newErrors.port = 'Le port est requis';
    } else if (isNaN(formData.port) || formData.port < 1 || formData.port > 65535) {
      newErrors.port = 'Le port doit être un nombre entre 1 et 65535';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Construire l'URL complète si elle n'a pas de port
      let finalUrl = formData.url;
      if (!finalUrl.includes(':' + formData.port) && formData.port) {
        // Extraire le protocole et l'hôte
        const urlParts = finalUrl.match(/^(https?:\/\/)([^\/]+)(.*)?$/);
        if (urlParts) {
          finalUrl = `${urlParts[1]}${urlParts[2].split(':')[0]}:${formData.port}${urlParts[3] || ''}`;
        }
      }

      onSave({
        ...formData,
        url: finalUrl,
        port: parseInt(formData.port)
      });
    }
  };

  const predefinedIcons = ['🖥️', '📊', '🔧', '📁', '🌐', '💾', '📈', '🔍', '⚙️', '📋'];
  const predefinedColors = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', 
    '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6B7280'
  ];

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              {app ? 'Modifier l\'application' : 'Nouvelle application'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nom */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom de l'application *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="ex: Parc Info"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="2"
                placeholder="Description de l'application"
              />
            </div>

            {/* URL et Port côte à côte */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL de base *
                </label>
                <input
                  type="text"
                  name="url"
                  value={formData.url}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.url ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="http://localhost"
                />
                {errors.url && <p className="text-red-500 text-xs mt-1">{errors.url}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Port *
                </label>
                <input
                  type="number"
                  name="port"
                  value={formData.port}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.port ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="3001"
                  min="1"
                  max="65535"
                />
                {errors.port && <p className="text-red-500 text-xs mt-1">{errors.port}</p>}
              </div>
            </div>
            
            <div className="text-sm text-gray-500">
              URL complète : {formData.url && formData.port ? `${formData.url.split(':').slice(0, 2).join(':')}//localhost:${formData.port}` : 'http://localhost:PORT'}
            </div>

            {/* Catégorie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Catégorie
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sans catégorie</option>
                <option value="Administration">Administration</option>
                <option value="Gestion">Gestion</option>
                <option value="Monitoring">Monitoring</option>
                <option value="Développement">Développement</option>
                <option value="Support">Support</option>
              </select>
            </div>

            {/* Icône */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Icône
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {predefinedIcons.map(icon => (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, icon }))}
                    className={`icon-button ${formData.icon === icon ? 'selected' : ''}`}
                  >
                    {icon}
                  </button>
                ))}
              </div>
              <input
                type="text"
                name="icon"
                value={formData.icon}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                placeholder="🔧"
              />
            </div>

            {/* Couleur */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Couleur
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {predefinedColors.map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, color }))}
                    className={`color-button ${formData.color === color ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleInputChange}
                className="w-full h-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Options */}
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Application active</span>
              </label>
              
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="requiresAuth"
                  checked={formData.requiresAuth}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Authentification requise</span>
              </label>
            </div>

            {/* Boutons */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {app ? 'Modifier' : 'Ajouter'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppFormModal;